import React, { useState, useEffect } from 'react';
import { api, API, getToken } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import { Megaphone, Send, Trash2, FileText, FileSpreadsheet, AlertTriangle, Check, Clock, BarChart3 } from 'lucide-react';

const TIPO_LABEL = { todos: 'Todos', departamentos: 'Departamentos', empleados: 'Empleados' };
const fmtDT = (ts) => ts ? new Date(ts).toLocaleString('es-ES', { timeZone: 'Europe/Madrid' }) : '-';

const ComunicadosTab = () => {
  const [comunicados, setComunicados] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [form, setForm] = useState({ titulo: '', cuerpo: '', destinatario_tipo: 'todos', departamento_ids: [], empleado_ids: [], urgente: false, fecha_caducidad: '' });
  const [informe, setInforme] = useState(null);
  const [infId, setInfId] = useState(null);

  const cargar = () => api.get('/admin/comunicados').then((r) => setComunicados(r.data)).catch(() => {});
  useEffect(() => {
    cargar();
    api.get('/admin/empleados').then((r) => setEmpleados(r.data)).catch(() => {});
    api.get('/admin/departamentos').then((r) => setDepartamentos(r.data)).catch(() => {});
  }, []);

  const toggle = (key, id) => setForm((f) => ({ ...f, [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id] }));

  const crear = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, fecha_caducidad: form.fecha_caducidad || null };
      await api.post('/admin/comunicados', payload);
      toast.success('Comunicado enviado');
      setForm({ titulo: '', cuerpo: '', destinatario_tipo: 'todos', departamento_ids: [], empleado_ids: [], urgente: false, fecha_caducidad: '' });
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear comunicado');
    }
  };

  const eliminar = async (id) => {
    try { await api.delete(`/admin/comunicados/${id}`); toast.success('Comunicado eliminado'); cargar(); }
    catch { toast.error('Error al eliminar'); }
  };

  const verInforme = async (id) => {
    try { const r = await api.get(`/admin/comunicados/${id}/informe`); setInforme(r.data); setInfId(id); }
    catch { toast.error('Error al cargar el informe'); }
  };

  const exportar = (id, formato) => {
    const params = new URLSearchParams({ formato, token: getToken() });
    window.open(`${API}/admin/comunicados/${id}/export?${params.toString()}`, '_blank');
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="comunicados-tab">
      <Card className="border-zinc-200 lg:col-span-1 h-fit">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2 text-lg">
            <Megaphone className="h-5 w-5" /> Nuevo comunicado
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={crear} className="space-y-3">
            <div className="space-y-1.5">
              <Label>Título</Label>
              <Input data-testid="com-titulo" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label>Mensaje</Label>
              <Textarea data-testid="com-cuerpo" rows={5} value={form.cuerpo} onChange={(e) => setForm({ ...form, cuerpo: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label>Destinatarios</Label>
              <Select value={form.destinatario_tipo} onValueChange={(v) => setForm({ ...form, destinatario_tipo: v })}>
                <SelectTrigger data-testid="com-destinatario-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los empleados</SelectItem>
                  <SelectItem value="departamentos">Por departamento</SelectItem>
                  <SelectItem value="empleados">Empleados concretos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.destinatario_tipo === 'departamentos' && (
              <div className="flex flex-wrap gap-2">
                {departamentos.map((d) => (
                  <button type="button" key={d.id} onClick={() => toggle('departamento_ids', d.id)}
                    data-testid={`com-dep-${d.id}`}
                    className={`text-xs px-2.5 py-1 rounded-full border ${form.departamento_ids.includes(d.id) ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-300'}`}>
                    {d.nombre}
                  </button>
                ))}
              </div>
            )}
            {form.destinatario_tipo === 'empleados' && (
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                {empleados.map((e) => (
                  <button type="button" key={e.id} onClick={() => toggle('empleado_ids', e.id)}
                    data-testid={`com-emp-${e.username}`}
                    className={`text-xs px-2.5 py-1 rounded-full border ${form.empleado_ids.includes(e.id) ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-300'}`}>
                    {e.full_name}
                  </button>
                ))}
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Caducidad (opcional)</Label>
              <Input type="date" data-testid="com-caducidad" value={form.fecha_caducidad} onChange={(e) => setForm({ ...form, fecha_caducidad: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
              <input type="checkbox" data-testid="com-urgente" checked={form.urgente} onChange={(e) => setForm({ ...form, urgente: e.target.checked })} />
              <AlertTriangle className="h-4 w-4 text-red-500" /> Marcar como urgente
            </label>
            <Button type="submit" className="w-full bg-zinc-900 hover:bg-zinc-800" data-testid="crear-comunicado-btn">
              <Send className="h-4 w-4 mr-2" /> Enviar comunicado
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="border-zinc-200 lg:col-span-2">
        <CardHeader><CardTitle className="font-heading text-lg">Comunicados ({comunicados.length})</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {comunicados.length === 0 && <p className="text-center text-zinc-400 py-8">Aún no hay comunicados.</p>}
          {comunicados.map((c) => (
            <div key={c.id} className="border border-zinc-200 rounded-xl p-4" data-testid={`comunicado-${c.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-zinc-900">{c.titulo}</span>
                    {c.urgente && <Badge className="bg-red-100 text-red-700 gap-1"><AlertTriangle className="h-3 w-3" /> Urgente</Badge>}
                    <Badge variant="outline">{TIPO_LABEL[c.destinatario_tipo]}</Badge>
                    {!c.vigente && <Badge variant="secondary">Caducado</Badge>}
                  </div>
                  <p className="text-sm text-zinc-600 mt-1 line-clamp-2 whitespace-pre-wrap">{c.cuerpo}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-zinc-500">
                    <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {fmtDT(c.created_at)}</span>
                    <span className="flex items-center gap-1 text-green-700"><Check className="h-3.5 w-3.5" /> {c.aceptados}/{c.total_destinatarios} aceptado(s)</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => verInforme(c.id)} data-testid={`informe-${c.id}`}>
                    <BarChart3 className="h-4 w-4 mr-1.5" /> Informe
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="icon" variant="outline" className="text-red-600" data-testid={`delete-com-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>¿Eliminar el comunicado?</AlertDialogTitle>
                        <AlertDialogDescription>Se borrará el comunicado y su registro de aceptaciones.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => eliminar(c.id)}>Eliminar</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={!!informe} onOpenChange={(v) => { if (!v) setInforme(null); }}>
        <DialogContent className="max-w-2xl" data-testid="informe-dialog">
          <DialogHeader><DialogTitle>Acuse de lectura: {informe?.comunicado?.titulo}</DialogTitle></DialogHeader>
          {informe && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Badge className="bg-green-100 text-green-800">{informe.aceptados.length} de {informe.total} aceptado(s)</Badge>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => exportar(infId, 'pdf')} data-testid="com-export-pdf"><FileText className="h-4 w-4 mr-1.5" /> PDF</Button>
                  <Button size="sm" variant="outline" onClick={() => exportar(infId, 'excel')} data-testid="com-export-excel"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel</Button>
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto space-y-3">
                <div>
                  <p className="text-xs font-semibold text-green-700 uppercase mb-1">Aceptados</p>
                  {informe.aceptados.length === 0 && <p className="text-sm text-zinc-400">Nadie ha aceptado todavía.</p>}
                  {informe.aceptados.map((a) => (
                    <div key={a.username} className="flex justify-between text-sm py-1 border-b border-zinc-100">
                      <span>{a.full_name}</span><span className="text-zinc-500 tabular-nums">{fmtDT(a.aceptado_at)}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-xs font-semibold text-amber-700 uppercase mb-1">Pendientes</p>
                  {informe.pendientes.length === 0 && <p className="text-sm text-zinc-400">Todos han aceptado.</p>}
                  {informe.pendientes.map((p) => (
                    <div key={p.username} className="text-sm py-1 border-b border-zinc-100 text-zinc-600">{p.full_name}</div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComunicadosTab;
